import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone } from "lucide-react";
import { SERVICE_AREAS, COMPANY, IMAGES } from "../data/site";
import { LANDING_PRODUCTS } from "../data/landing";
import { useReveal } from "../hooks/use-reveal";
import { useSeo } from "../hooks/use-seo";
import { ContactForm } from "../components/sections/ContactForm";

export default function ServiceAreasIndex() {
    useReveal();
    useSeo({
        title: "Service Areas | HAZ Insulation · Chicago Southland & NW Indiana",
        description:
            "HAZ Insulation serves Tinley Park, Orland Park, Frankfort, Mokena, Lemont, Highland IN, Schererville IN and 12 more cities across Chicago Southland and NW Indiana.",
    });

    const il = SERVICE_AREAS.filter((a) => a.state === "IL");
    const inAreas = SERVICE_AREAS.filter((a) => a.state === "IN");

    return (
        <main data-testid="service-areas-index" className="pt-24">
            <section className="relative py-24 md:py-32 overflow-hidden">
                <div className="absolute inset-0 -z-10">
                    <img
                        src={IMAGES.chicago}
                        alt=""
                        className="w-full h-full object-cover opacity-25"
                    />
                    <div className="absolute inset-0 bg-ink/85" />
                </div>
                <div className="max-w-7xl mx-auto px-6 md:px-12 text-bone">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-5 reveal">
                        <span className="h-px w-10 bg-ember inline-block align-middle mr-3" />
                        Service Areas
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[0.98] max-w-4xl reveal">
                        Chicago Southland &amp;<br />
                        <span className="text-ember">Northwest Indiana — that&apos;s home.</span>
                    </h1>
                    <p className="mt-8 text-lg md:text-xl text-bone/85 max-w-3xl leading-relaxed reveal">
                        We don&apos;t chase out-of-area jobs. We focus on the corridor we know
                        cold — older Chicago bungalows, suburban ranches, and new-builds across the
                        south suburbs and into NW Indiana.
                    </p>
                </div>
            </section>

            <section className="py-20 md:py-28 bg-bone">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-moss mb-5 reveal">
                        Illinois
                    </div>
                    <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-10 reveal">
                        {il.length} Illinois cities served.
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-line border border-line">
                        {il.map((a) => (
                            <Link
                                key={a.slug}
                                to={`/areas/${a.slug}`}
                                data-testid={`area-index-link-${a.slug}`}
                                className="bg-bone hover:bg-white p-6 flex items-center gap-3 group transition-colors"
                            >
                                <MapPin size={18} strokeWidth={1.5} className="text-copper shrink-0" />
                                <div>
                                    <div className="font-heading font-bold text-base group-hover:text-copper transition-colors">
                                        {a.city}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted_ink">
                                        {a.state} · {a.zip}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>

                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-moss mb-5 mt-16 reveal">
                        Northwest Indiana
                    </div>
                    <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-10 reveal">
                        {inAreas.length} Indiana cities served.
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-line border border-line">
                        {inAreas.map((a) => (
                            <Link
                                key={a.slug}
                                to={`/areas/${a.slug}`}
                                className="bg-bone hover:bg-white p-6 flex items-center gap-3 group transition-colors"
                            >
                                <MapPin size={18} strokeWidth={1.5} className="text-copper shrink-0" />
                                <div>
                                    <div className="font-heading font-bold text-base group-hover:text-copper transition-colors">
                                        {a.city}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted_ink">
                                        {a.state} · {a.zip}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* By keyword */}
            <section className="py-20 md:py-28 bg-moss text-bone">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-5 reveal">
                        Find your service + city
                    </div>
                    <h2 className="font-heading text-3xl md:text-5xl font-bold leading-[1.05] tracking-tight mb-10 reveal">
                        Browse by what you&apos;re looking for.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {LANDING_PRODUCTS.map((p) => (
                            <div key={p.keyword} className="bg-ink p-6 reveal">
                                <h3 className="font-heading text-lg font-bold mb-3 text-ember">
                                    {p.label}
                                </h3>
                                <ul className="space-y-1.5 text-sm text-bone/80">
                                    {SERVICE_AREAS.slice(0, 6).map((a) => {
                                        const stateLower = a.state.toLowerCase();
                                        const slug = `${p.keyword}-${a.slug}${
                                            a.slug.endsWith(`-${stateLower}`) ? "" : `-${stateLower}`
                                        }`;
                                        return (
                                            <li key={a.slug}>
                                                <Link
                                                    to={`/${slug}`}
                                                    className="link-underline hover:text-ember"
                                                >
                                                    {a.city}, {a.state}
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ))}
                    </div>
                    <div className="mt-12 reveal">
                        <Link
                            to="/sitemap"
                            className="btn-lift inline-flex items-center gap-2 bg-bone text-ink font-semibold py-4 px-8 hover:bg-ember"
                        >
                            See every page on the site <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            </section>

            <ContactForm />
        </main>
    );
}
